import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';

import { PlatformAdmins } from './PlatformAdmins';
import { AccessRequests } from './AccessRequests';

/**
 * Access — the superadmin access-management surfaces (the platform-admin
 * roster + the inbound access-request queue) consolidated into one tabbed
 * page. Each panel is the original page component in `embedded` mode (its own
 * h4/description suppressed; page-level actions like Refresh / Invite kept).
 * The active tab lives in the URL (`?tab=admins|requests`) so the old routes
 * deep-link straight to the right tab.
 */
const TABS = [
  {
    key: 'admins',
    label: 'Platform admins',
    render: (slot: HTMLElement | null) => (
      <PlatformAdmins embedded actionsSlot={slot} />
    ),
  },
  {
    key: 'requests',
    label: 'Access requests',
    render: () => <AccessRequests embedded />,
  },
] as const;

export function Access() {
  const [params, setParams] = useSearchParams();
  // Host for the active tab's page-level actions (Refresh / Invite),
  // portaled up onto the tab line — same pattern as tenant Settings.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const requested = params.get('tab');
  const active = Math.max(
    0,
    TABS.findIndex((t) => t.key === requested),
  );

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h4">Access</Typography>
        <Typography variant="body2" color="text.secondary">
          Platform admins and inbound access requests.
        </Typography>
      </Stack>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={active}
          onChange={(_e, v: number) =>
            setParams({ tab: TABS[v].key }, { replace: true })
          }
          aria-label="Access sections"
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box
          ref={setActionsSlot}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}
        />
      </Box>

      <Box>{TABS[active].render(actionsSlot)}</Box>
    </Stack>
  );
}
